import { FlowQuestion, AdminFlow, FlowMetadata } from './types';
import type { FlowThemeInheritance } from './FlowThemePicker';
import { DEFAULT_FLOW_THEME_ID } from '../../../components/QuestionFlow/themes/flowThemeRegistry';

export const FREQUENCY_CHOICES = ['once', 'daily', 'weekly'] as const;

export const FREQUENCY_OPTIONS = [
  { value: 'once', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
] as const;

export const normalizeFrequency = (value?: string | null): (typeof FREQUENCY_CHOICES)[number] => {
  const normalized = (value ?? '').trim();
  return FREQUENCY_CHOICES.includes(normalized as (typeof FREQUENCY_CHOICES)[number])
    ? (normalized as (typeof FREQUENCY_CHOICES)[number])
    : FREQUENCY_CHOICES[0];
};

export const DEFAULT_FLOW_METADATA: Partial<AdminFlow> = {
  name: '',
  title: '',
  description: '',
  frequency: normalizeFrequency(FREQUENCY_CHOICES[0]),
  is_active: true,
  is_public: false,
  media: null,
  media_asset_id: null,
  is_quiz: false,
  voice_mode_enabled: false,
  quiz_mode: 'graded',
  passing_score_percentage: 70,
  max_attempts: 2,
  allow_retake_after_pass: false,
  randomize_question_order: true,
  randomize_option_order: true,
  estimated_duration_minutes: null,
  theme: '',
  questions: [],
};

export const isValidEstimatedDuration = (value?: number | string | null): boolean => {
  const text = String(value ?? '').trim();
  return text === '' || (/^\d+$/.test(text) && Number.isSafeInteger(Number(text)) && Number(text) > 0);
};

export const deriveQuizMode = (flow: Partial<AdminFlow>): 'graded' | 'pass_only' => {
  if (flow.is_quiz && flow.passing_score_percentage === null) {
    return 'pass_only';
  }
  return 'graded';
};

export const reindexQuestions = (items: FlowQuestion[]) =>
  items.map((item, index) => ({
    ...item,
    order: index,
  }));

export const toOrderedQuestions = (items?: FlowQuestion[] | null) =>
  reindexQuestions((items ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));


/** What a flow's "Inherit from course" theme option resolves to, from the admin payload. */
export const getFlowThemeInheritance = (
  flow: Pick<FlowMetadata, 'inherited_theme' | 'inherited_theme_source' | 'inherited_theme_course' | 'theme_courses'>,
): FlowThemeInheritance => {
  // Compare what learners actually see from each course: a course without a
  // theme shows the default, so a themed and an unthemed course disagree.
  const courses = flow.theme_courses ?? [];
  const effectiveThemes = new Set(courses.map((course) => course.theme || DEFAULT_FLOW_THEME_ID));
  return {
    theme: flow.inherited_theme ?? null,
    source: flow.inherited_theme_source ?? 'default',
    courseName: flow.inherited_theme_course?.name ?? null,
    varies: courses.length > 1 && effectiveThemes.size > 1,
  };
};

/**
 * A module's name for lists and headers: the learner-facing title (or name) without a trailing
 * "[content-key vN]" or a repeated course-name prefix, which factory-built modules carry.
 */
export const moduleDisplayName = (
  module: { name: string; title?: string | null },
  courseName?: string | null,
): { name: string; version: string } => {
  let name = (module.title || module.name || '').trim();
  let version = '';
  const versionMatch = name.match(/\s*\[([^\]]+)\]\s*$/);
  if (versionMatch && versionMatch.index !== undefined) {
    version = versionMatch[1];
    name = name.slice(0, versionMatch.index).trim();
  }
  if (courseName) {
    for (const separator of [' — ', ' | ', ': ']) {
      const prefix = `${courseName}${separator}`.toLowerCase();
      if (name.toLowerCase().startsWith(prefix)) {
        name = name.slice(prefix.length).trim();
      }
    }
  }
  return { name: name || module.name, version };
};
